// 📋 복사 기능 관리 훅
import { useState, useCallback } from 'react';
import type { CopyField } from '../types/applicationStickyHeader.types';

export interface UseApplicationCopyReturn {
  copiedField: CopyField | null;
  snackbarOpen: boolean;
  setSnackbarOpen: (open: boolean) => void;
  handleCopy: (value: string, fieldName: CopyField) => Promise<void>;
  getCopyIconStyles: (field: CopyField, size?: number) => object;
  getCopyTooltipTitle: (field: CopyField) => string;
}

/**
 * 애플리케이션 정보 복사 기능을 관리하는 훅
 * - 클립보드 복사
 - 복사 상태 관리 
 * - 시각적 피드백 (성공 아이콘, 색상 변경)
 */
export const useApplicationCopy = (): UseApplicationCopyReturn => {
  const [copiedField, setCopiedField] = useState<CopyField | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // 📋 복사 핸들러
  const handleCopy = useCallback(async (value: string, fieldName: CopyField) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(fieldName);
      setSnackbarOpen(true);
      
      // 2초 후 체크 아이콘 리셋
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('복사 실패:', error);
    }
  }, []);

  // 🎨 복사 아이콘 스타일 생성기
  const getCopyIconStyles = useCallback((field: CopyField, size: number = 24) => ({
    bgcolor: copiedField === field ? 'success.main' : 'grey.100',
    color: copiedField === field ? 'white' : 'grey.600',
    width: size,
    height: size,
    '&:hover': {
      bgcolor: copiedField === field ? 'success.dark' : 'grey.200',
      transform: 'scale(1.05)',
    },
    transition: 'all 0.2s ease',
  }), [copiedField]);

  // 🏷️ 툴팁 제목 생성기
  const getCopyTooltipTitle = useCallback((field: CopyField) => {
    if (copiedField === field) return '복사됨!';
    
    switch (field) {
      case 'address':
        return '주소 복사';
      case 'bank':
        return '계좌정보 복사';
      case 'delivery_tracking':
      case 'return_tracking':
        return '송장번호 복사';
      default:
        return '클립보드에 복사';
    }
  }, [copiedField]);

  return {
    copiedField,
    snackbarOpen,
    setSnackbarOpen,
    handleCopy,
    getCopyIconStyles,
    getCopyTooltipTitle,
  };
};